import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import * as A from './customerActions';
import type {
  Channel,
  Customer,
  CustomerFormInput,
  CustomerGroup,
  FlagAuditEntry,
  LifecycleState,
  ReasonCode,
  RiskFlag,
  RiskStatus,
  RuleGroup,
  RuleOption,
  Segment,
} from '../../components/customers/data/types';
import { LIFECYCLE_LABELS, RISK_STATUS_LABELS } from '../../components/customers/data/types';
import { genId, normalizeEmail, normalizePhone } from '../../components/customers/utils/normalize';
import { buildOptOutRecord } from './customerConsent';
import { DAYPART_OPTIONS, ORDER_CHANNEL_OPTIONS, mockCategories, mockItems, mockOutlets, mockTiers } from './customerReference';
import type { EvaluateContext } from './customerSegmentEngine';
import type { OptionSource } from './customerSegmentRules';

/** Display name recorded as the actor on audit entries (no auth layer in V1). */
export const CURRENT_ACTOR = 'You';

export interface FlagInput {
  status: Exclude<RiskStatus, 'none'>;
  reasonCode: ReasonCode;
  note?: string;
}

/** Build a full Customer from form input (used by create + CSV import). */
export function buildCustomer(input: Partial<CustomerFormInput> & { name: string }): Customer {
  const now = new Date().toISOString();
  return {
    id: genId(),
    name: input.name.trim(),
    phone: input.phone?.trim() || undefined,
    email: input.email?.trim() || undefined,
    birthday: input.birthday || undefined,
    notes: input.notes?.trim() || undefined,
    tags: input.tags ?? [],
    riskStatus: 'none',
    originChannel: 'back_office_manual',
    lifetimeSpend: 0,
    visitCount: 0,
    orders: [],
    appInstalled: false,
    pushEnabled: false,
    auditLog: [],
    lifecycleState: 'active',
    preferences: input.preferences?.trim() || undefined,
    dietaryNotes: input.dietaryNotes?.trim() || undefined,
    allergyNotes: input.allergyNotes?.trim() || undefined,
    importantDates: input.importantDates ?? [],
    loyaltyStatus: null,
    favouriteItems: [],
    customAttributes: input.customAttributes ?? [],
    groupIds: [],
    // Consent is captured from the customer; a record created here starts with none (CON-13).
    marketingConsent: [],
    savedCards: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function useCustomersStore() {
  const dispatch = useAppDispatch();
  const customers = useAppSelector((s) => s.customers.customers);

  const getById = useCallback(
    (id: string | undefined) => (id ? customers.find((c) => c.id === id) ?? null : null),
    [customers],
  );

  const findDuplicates = useCallback(
    (phone: string, email: string, excludeId?: string): Customer[] => {
      const p = normalizePhone(phone);
      const e = normalizeEmail(email);
      if (!p && !e) return [];
      return customers.filter((c) => {
        if (excludeId && c.id === excludeId) return false;
        const phoneMatch = Boolean(p) && normalizePhone(c.phone) === p;
        const emailMatch = Boolean(e) && normalizeEmail(c.email) === e;
        return phoneMatch || emailMatch;
      });
    },
    [customers],
  );

  const addCustomer = useCallback(
    (input: CustomerFormInput): Customer => {
      const customer = buildCustomer(input);
      dispatch(A.addCustomer(customer));
      return customer;
    },
    [dispatch],
  );

  const updateCustomer = useCallback(
    (id: string, updates: Partial<Customer>) => dispatch(A.updateCustomer(id, updates)),
    [dispatch],
  );

  const removeCustomer = useCallback((id: string) => dispatch(A.removeCustomer(id)), [dispatch]);

  const setFlag = useCallback(
    (customer: Customer, input: FlagInput) => {
      const now = new Date().toISOString();
      const flag: RiskFlag = { status: input.status, reasonCode: input.reasonCode, note: input.note?.trim() || undefined, setBy: CURRENT_ACTOR, setAt: now };
      const hadFlag = customer.riskStatus !== 'none';
      const auditEntry: FlagAuditEntry = {
        id: genId('aud'), action: hadFlag ? 'changed' : 'set', fromStatus: customer.riskStatus, toStatus: input.status,
        reasonCode: input.reasonCode, note: input.note?.trim() || undefined, actor: CURRENT_ACTOR, timestamp: now,
      };
      dispatch(A.setFlag({ id: customer.id, flag, newStatus: input.status, auditEntry }));
    },
    [dispatch],
  );

  const clearFlag = useCallback(
    (customer: Customer, note: string) => {
      const now = new Date().toISOString();
      const auditEntry: FlagAuditEntry = {
        id: genId('aud'), action: 'cleared', fromStatus: customer.riskStatus, toStatus: 'none',
        note: note.trim() || undefined, actor: CURRENT_ACTOR, timestamp: now,
      };
      dispatch(A.clearFlag({ id: customer.id, auditEntry }));
    },
    [dispatch],
  );

  /** CON-12 — staff record an opt-out for one channel when the customer asks. */
  const recordOptOut = useCallback(
    (customerId: string, channel: Channel) =>
      dispatch(A.recordOptOut({ id: customerId, record: buildOptOutRecord(genId('con'), channel, 'portal_staff', CURRENT_ACTOR, new Date().toISOString()) })),
    [dispatch],
  );

  // ─── V2 data operations ───
  const bulkTag = useCallback((ids: string[], tags: string[]) => dispatch(A.bulkTag(ids, tags)), [dispatch]);
  const setLifecycle = useCallback((ids: string[], lifecycle: LifecycleState) => dispatch(A.setLifecycle(ids, lifecycle)), [dispatch]);
  const commitMerge = useCallback(
    (keepId: string, merged: Customer, removeIds: string[]) => dispatch(A.mergeCustomers({ keepId, merged, removeIds })),
    [dispatch],
  );
  const importCustomers = useCallback((rows: Array<Partial<CustomerFormInput> & { name: string }>) => {
    const built = rows.map((r) => buildCustomer(r));
    dispatch(A.importCustomers(built));
    return built;
  }, [dispatch]);

  // ─── P2: saved cards ───
  const addCard = useCallback((customerId: string, input: { brand: string; last4: string; expMonth: number; expYear: number; isDefault?: boolean }) => {
    dispatch(A.addCard(customerId, { id: genId('card'), brand: input.brand, last4: input.last4, expMonth: input.expMonth, expYear: input.expYear, isDefault: Boolean(input.isDefault), addedAt: new Date().toISOString() }));
  }, [dispatch]);
  const removeCard = useCallback((customerId: string, cardId: string) => dispatch(A.removeCard(customerId, cardId)), [dispatch]);
  const setDefaultCard = useCallback((customerId: string, cardId: string) => dispatch(A.setDefaultCard(customerId, cardId)), [dispatch]);

  return useMemo(
    () => ({ customers, getById, findDuplicates, addCustomer, updateCustomer, removeCustomer, setFlag, clearFlag, recordOptOut, bulkTag, setLifecycle, commitMerge, importCustomers, addCard, removeCard, setDefaultCard }),
    [customers, getById, findDuplicates, addCustomer, updateCustomer, removeCustomer, setFlag, clearFlag, recordOptOut, bulkTag, setLifecycle, commitMerge, importCustomers, addCard, removeCard, setDefaultCard],
  );
}

export function useGroupsStore() {
  const dispatch = useAppDispatch();
  const groups = useAppSelector((s) => s.customers.groups);

  const getById = useCallback((id: string | undefined) => (id ? groups.find((g) => g.id === id) ?? null : null), [groups]);

  const addGroup = useCallback((name: string, description?: string, memberIds: string[] = []): CustomerGroup => {
    const now = new Date().toISOString();
    const group: CustomerGroup = { id: genId('grp'), name: name.trim(), description: description?.trim() || undefined, memberIds, createdAt: now, updatedAt: now };
    dispatch(A.addGroup(group));
    return group;
  }, [dispatch]);

  const updateGroup = useCallback((id: string, updates: Partial<CustomerGroup>) => dispatch(A.updateGroup(id, updates)), [dispatch]);
  const removeGroup = useCallback((id: string) => dispatch(A.removeGroup(id)), [dispatch]);
  const setMembers = useCallback((groupId: string, memberIds: string[]) => dispatch(A.setGroupMembers(groupId, memberIds)), [dispatch]);
  const bulkAddToGroup = useCallback((groupId: string, ids: string[]) => dispatch(A.bulkAddToGroup(groupId, ids)), [dispatch]);

  return useMemo(
    () => ({ groups, getById, addGroup, updateGroup, removeGroup, setMembers, bulkAddToGroup }),
    [groups, getById, addGroup, updateGroup, removeGroup, setMembers, bulkAddToGroup],
  );
}

export function useSegmentsStore() {
  const dispatch = useAppDispatch();
  const segments = useAppSelector((s) => s.customers.segments);

  const getById = useCallback((id: string | undefined) => (id ? segments.find((s) => s.id === id) ?? null : null), [segments]);

  const addSegment = useCallback((name: string, description: string, rules: RuleGroup): Segment => {
    const now = new Date().toISOString();
    const segment: Segment = { id: genId('seg'), name: name.trim(), description: description.trim(), type: 'custom', prebuiltKey: null, rules, createdAt: now, updatedAt: now };
    dispatch(A.addSegment(segment));
    return segment;
  }, [dispatch]);

  const updateSegment = useCallback((id: string, updates: Partial<Segment>) => dispatch(A.updateSegment(id, updates)), [dispatch]);
  const removeSegment = useCallback((id: string) => dispatch(A.removeSegment(id)), [dispatch]);

  return useMemo(
    () => ({ segments, getById, addSegment, updateSegment, removeSegment }),
    [segments, getById, addSegment, updateSegment, removeSegment],
  );
}

const RISK_OPTIONS: RuleOption[] = (['none', 'watch', 'blocked'] as const).map((id) => ({ id, name: id === 'none' ? 'No flag' : RISK_STATUS_LABELS[id] }));
const LIFECYCLE_OPTIONS: RuleOption[] = (['active', 'inactive', 'archived'] as const).map((id) => ({ id, name: LIFECYCLE_LABELS[id] }));

/** Choices for list-valued segment rule fields, keyed by the field's option source. */
export function useSegmentRuleOptions(): Record<OptionSource, RuleOption[]> {
  const customers = useAppSelector((s) => s.customers.customers);
  const groups = useAppSelector((s) => s.customers.groups);
  const campaigns = useAppSelector((s) => s.campaigns.campaigns);
  return useMemo(() => {
    const tags = Array.from(new Set(customers.flatMap((c) => c.tags))).sort((a, b) => a.localeCompare(b));
    return {
      items: mockItems,
      categories: mockCategories,
      outlets: mockOutlets,
      orderChannels: ORDER_CHANNEL_OPTIONS,
      dayparts: DAYPART_OPTIONS,
      tiers: mockTiers,
      campaigns: campaigns.filter((c) => c.kind === 'one_time' && c.lastSentAt).map((c) => ({ id: c.id, name: c.name })),
      tags: tags.map((t) => ({ id: t, name: t })),
      groups: groups.map((g) => ({ id: g.id, name: g.name })),
      riskStatuses: RISK_OPTIONS,
      lifecycleStates: LIFECYCLE_OPTIONS,
    };
  }, [customers, groups, campaigns]);
}

/** What segment rules are checked against besides the customers themselves: today, and who received which campaign. */
export function useSegmentContext(): EvaluateContext {
  const received = useAppSelector((s) => s.campaigns.receivedByCustomer);
  const redeemed = useAppSelector((s) => s.campaigns.redeemedByCustomer);
  return useMemo(() => ({ now: new Date(), history: { received, redeemed } }), [received, redeemed]);
}

export function useCustomerToast() {
  const dispatch = useAppDispatch();
  const toasts = useAppSelector((s) => s.customers.toasts);

  const addToast = useCallback(
    (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      dispatch(A.toastAdd({ id, message, type }));
      setTimeout(() => dispatch(A.toastRemove(id)), 3000);
    },
    [dispatch],
  );

  const removeToast = useCallback((id: string) => dispatch(A.toastRemove(id)), [dispatch]);

  return useMemo(() => ({ toasts, addToast, removeToast }), [toasts, addToast, removeToast]);
}
